import { ChangeEvent, FormEvent, ReactElement, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NewButton from "../../../components/Atomos/Buttons/newButton";
import {
  getManageDealCountries,
  getManageDealEditor,
  ManageDealActor,
  ManageDealEditor,
  ManageDealOption,
  saveManageDeal,
} from "../../../Services/ManageDealmaker";
import { DealBasics, DealNarrative } from "./ManageDealmakerFields";

interface ManageDealmakerFormProps {
  actor: ManageDealActor;
  mode: "add" | "update";
  dealId: string;
}

function selectedIds(event: ChangeEvent<HTMLSelectElement>): number[] {
  return Array.from(event.target.selectedOptions).map((option) => Number(option.value)).filter((id) => id > 0);
}

function toggleNotify(current: number[], id: number): number[] {
  if (id === 9) {
    return current.includes(9) ? [] : [9];
  }
  const rest = current.filter((item) => item !== 9 && item !== id);
  if (current.includes(id)) {
    return rest;
  }
  return [...rest, id];
}

export default function ManageDealmakerForm({ actor, mode, dealId }: ManageDealmakerFormProps): ReactElement {
  const navigate = useNavigate();
  const [editor, setEditor] = useState<ManageDealEditor | null>(null);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [dealContactId, setDealContactId] = useState(0);
  const [openEnded, setOpenEnded] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [title, setTitle] = useState("");
  const [dealTypeId, setDealTypeId] = useState(0);
  const [customDealType, setCustomDealType] = useState("");
  const [dealAmount, setDealAmount] = useState("");
  const [governingLaw, setGoverningLaw] = useState("");
  const [regionIds, setRegionIds] = useState<number[]>([]);
  const [countryIds, setCountryIds] = useState<number[]>([]);
  const [countries, setCountries] = useState<ManageDealOption[]>([]);
  const [industryIds, setIndustryIds] = useState<number[]>([]);
  const [allIndustries, setAllIndustries] = useState(false);
  const [description, setDescription] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [notifyRegions, setNotifyRegions] = useState<number[]>(mode === "add" ? [9] : []);

  useEffect(() => {
    let cancelled = false;
    getManageDealEditor(actor, mode === "update" ? dealId : "", "")
      .then((result) => {
        if (cancelled) {
          return;
        }
        setEditor(result);
        setCountries(result.countries);
        const draft = result.deal;
        if (!draft) {
          return;
        }
        setDealContactId(draft.dealContactId);
        setOpenEnded(draft.openEnded);
        setDateFrom(draft.dateFrom);
        setDateTo(draft.dateTo);
        setTitle(draft.title);
        setDealTypeId(draft.dealTypeId);
        setDealAmount(draft.dealAmount);
        setGoverningLaw(draft.governingLaw);
        setRegionIds(draft.regionIds);
        setCountryIds(draft.countryIds);
        setIndustryIds(draft.industryIds);
        setAllIndustries(draft.allIndustries);
        setDescription(draft.description);
        setAdditionalInfo(draft.additionalInfo);
        setNotifyRegions(draft.notifyRegions.length > 0 ? draft.notifyRegions : [9]);
      })
      .catch(() => {
        if (!cancelled) {
          setError("Firm deal form could not be loaded.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [actor, mode, dealId]);

  async function onRegions(event: ChangeEvent<HTMLSelectElement>): Promise<void> {
    const next = selectedIds(event);
    setRegionIds(next);
    try {
      const loaded = await getManageDealCountries(actor, (next.length > 0 ? next : [1, 2, 3, 4, 5, 6, 7, 8]).join(","));
      setCountries(loaded);
    } catch {
      setCountries([]);
    }
  }

  function onAllIndustries(checked: boolean): void {
    setAllIndustries(checked);
    setIndustryIds(checked ? (editor?.industries || []).map((row) => row.id) : []);
  }

  async function onSubmit(): Promise<void> {
    setErrors([]);
    let result;
    try {
      result = await saveManageDeal(actor, {
      mode,
      dealId: mode === "update" ? Number(dealId) : null,
      dealContactId,
      openEnded,
      dateFrom,
      dateTo,
      title,
      dealTypeId,
      customDealType,
      dealAmount,
      governingLaw,
      countryIds,
      industryIds,
      allIndustries,
      description,
      additionalInfo,
      notifyRegions,
    });
    } catch {
      setErrors(["The deal could not be saved."]);
      return;
    }
    if (!result.saved) {
      setErrors(result.errors || ["The deal could not be saved."]);
      return;
    }
    setMessage(result.message);
  }

  if (message) {
    return (
      <div className="stack">
        <p className="form-status border border-gray-300 p-6 text-center">{message}</p>
        <NewButton text="Ok" onClick={() => navigate("/manage_dealmaker")} />
      </div>
    );
  }
  if (error) {
    return <p className="form-status">{error}</p>;
  }
  if (!editor) {
    return <p className="form-status">Loading the deal form.</p>;
  }

  return (
    <form className="stack" onSubmit={(event: FormEvent) => { event.preventDefault(); void onSubmit(); }}>
      <h2>{mode === "add" ? "Submit a New Deal" : "Edit Deal"}</h2>
      <p className="form-status">All fields marked with * are required</p>
      {errors.length > 0 ? (
        <div className="border border-gray-300 p-4 text-primary">
          <p className="font-semibold">Please correct the following error(s)</p>
          {errors.map((item) => <p key={item}>{item}</p>)}
        </div>
      ) : null}
      <DealBasics
        editor={editor}
        countries={countries}
        dealContactId={dealContactId}
        openEnded={openEnded}
        dateFrom={dateFrom}
        dateTo={dateTo}
        title={title}
        dealTypeId={dealTypeId}
        customDealType={customDealType}
        dealAmount={dealAmount}
        governingLaw={governingLaw}
        regionIds={regionIds}
        countryIds={countryIds}
        onContact={setDealContactId}
        onOpenEnded={setOpenEnded}
        onDateFrom={setDateFrom}
        onDateTo={setDateTo}
        onTitle={setTitle}
        onDealType={setDealTypeId}
        onCustomType={setCustomDealType}
        onAmount={setDealAmount}
        onLaw={setGoverningLaw}
        onRegions={(event) => { void onRegions(event); }}
        onCountries={(event) => setCountryIds(selectedIds(event))}
      />
      <DealNarrative
        editor={editor}
        description={description}
        additionalInfo={additionalInfo}
        industryIds={industryIds}
        allIndustries={allIndustries}
        notifyRegions={notifyRegions}
        onDescription={setDescription}
        onAdditional={setAdditionalInfo}
        onAll={onAllIndustries}
        onIndustry={(id, checked) => {
          setAllIndustries(false);
          setIndustryIds(checked ? [...industryIds, id] : industryIds.filter((item) => item !== id));
        }}
        onNotify={(id) => setNotifyRegions(toggleNotify(notifyRegions, id))}
        onSubmit={() => { void onSubmit(); }}
        onCancel={() => navigate("/manage_dealmaker")}
      />
    </form>
  );
}
